import * as React from "react";
import { Container, Button, Card, CardContent, CardHeader, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import { useUpdateVideoStreamingEnabledMutation } from "@/graphql/hooks/organizations-hook";
import { useCurrentOrganization } from "@/context/OrganizationProvider";
import { enqueueSnackbar } from "notistack";

export default function OrganizationSettingsView() {
  const [updateResult, updateVideoStreamingEnabled] = useUpdateVideoStreamingEnabledMutation();
  const loading = updateResult.fetching;
  const { currentOrganization } = useCurrentOrganization();
  const [showEnableConfirm, setShowEnableConfirm] = React.useState(false);
  const [showEnableResult, setShowEnableResult] = React.useState(false);
  const [scheduledCount, setScheduledCount] = React.useState<number>(0);

  const handleEnableDisableClick = async () => {
    if (!currentOrganization) return;
    if (currentOrganization.videoStreamingEnabled) {
      // Disabling: no dialogs, just call mutation
      const result = await updateVideoStreamingEnabled({ input: { organizationId: currentOrganization.id, enabled: false } });
      if( result.error ) {
        enqueueSnackbar(result.error.message, { variant: 'error' });
        return;
      }
      return;
    }
    // Enabling: show confirm first
    setShowEnableConfirm(true);
  };

  const handleConfirmEnable = async () => {
    if (!currentOrganization) return;
    const result = await updateVideoStreamingEnabled({ input: { organizationId: currentOrganization.id, enabled: true } });
    if( result.error ) {
      enqueueSnackbar(result.error.message, { variant: 'error' });
      return;
    }
    const count = result.data?.jrnyUpdateOrganizationVideoStreamingEnabled.videoFilesToProcess ?? 0;
    setScheduledCount(count);
    setShowEnableConfirm(false);
    setShowEnableResult(true);
  };

  const handleCancelEnable = () => setShowEnableConfirm(false);
  const handleCloseResult = () => setShowEnableResult(false);
  return (
    <Container>
      <Card>
        <CardHeader title="Organization Settings" />
        <CardContent>
          <Button variant="contained" color="primary" onClick={handleEnableDisableClick} disabled={loading}>
            {currentOrganization?.videoStreamingEnabled ? "Disable" : "Enable"} Video Streaming
          </Button>
          <Typography variant="body2">
            {currentOrganization?.videoStreamingEnabled ? "Video streaming is enabled." : "Video streaming is disabled."}
          </Typography>
        </CardContent>
      </Card>
      <Dialog open={showEnableConfirm} onClose={handleCancelEnable}>
        <DialogTitle>Enable Video Streaming?</DialogTitle>
        <DialogContent>
          <Typography>
            Enabling streaming will queue existing video files for processing. This may take time and incur costs.
            Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEnable} disabled={loading}>Cancel</Button>
          <Button onClick={handleConfirmEnable} variant="contained" color="primary" disabled={loading}>Enable</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEnableResult} onClose={handleCloseResult}>
        <DialogTitle>Streaming Enabled</DialogTitle>
        <DialogContent>
          <Typography>
            {scheduledCount} video file{scheduledCount === 1 ? "" : "s"} scheduled for processing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResult} autoFocus>OK</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}


